// utils/gifFillPlayback.js — T22.145: when does a GIF SEGMENT FILL animate?
//
// Gordon 2026-10-12: "GIF segment fills must ANIMATE automatically (Edit Wheel
// + View Wheel), not only on open/hover. Fallback: when memory is a concern
// (many GIF fills on one wheel, or navigator.deviceMemory low), switch to
// play-on-hover only with a poster frame. prefers-reduced-motion keeps the
// poster frame."
//
// ONE policy shared by both wheel renderers (renderer-v2 = View Wheel,
// D3Wheels/createPie = Edit Wheel), so the two can never disagree:
//   "autoplay" — the wedge paints the REAL gif and plays at rest.
//   "hover"    — the wedge paints a still POSTER; it plays only while hovered
//                (or selected / open).
//   "still"    — prefers-reduced-motion: the poster, always.
//
// THRESHOLD (measured live on web-stg.iyuga.net, main.d1167c7d, 1440px,
// 320x320 24-frame GIFs, renderer-process RSS / CPU over 3 s, all at rest):
//   GIF fills | View Wheel        | Edit Wheel
//   1         | 722 MB /  34 %    | 732 MB /  63 %
//   8         | 791 MB /  21 %    | 836 MB / 108 %
//   32        | 954 MB /  38 %    | 1104 MB / 300 % (+ a 50 ms frame)
// JS heap stays ~30 MB in every case (decoded GIF frames live outside it), so
// RSS + CPU is the signal. Up to 8 GIFs costs ~+70-100 MB and about one core on
// the heavier Edit Wheel; 32 costs +230-370 MB and three cores. So: autoplay up
// to GIF_AUTOPLAY_MAX_FILLS = 8, hover-only above it.
// navigator.deviceMemory (Chromium; GB, bucketed 0.25..8) <= 2 is a low-memory
// device -> hover-only regardless of count. Browsers without deviceMemory
// (Safari, Firefox) rely on the count rule alone.

import { extractGifPoster } from "../components/SegmentBuilder/Shared/SegmentStyling/logic/gifPoster";

export const GIF_AUTOPLAY_MAX_FILLS = 8;
export const GIF_LOW_DEVICE_MEMORY_GB = 2;

export const GIF_PLAYBACK = Object.freeze({
  AUTOPLAY: "autoplay",
  HOVER: "hover",
  STILL: "still",
});

/**
 * Pure policy.
 * @param {{gifCount:number, deviceMemory?:number, reducedMotion?:boolean}} p
 * @returns {"autoplay"|"hover"|"still"}
 */
export function gifPlaybackMode({ gifCount, deviceMemory, reducedMotion } = {}) {
  if (reducedMotion) return GIF_PLAYBACK.STILL;
  if (
    typeof deviceMemory === "number" &&
    deviceMemory > 0 &&
    deviceMemory <= GIF_LOW_DEVICE_MEMORY_GB
  ) {
    return GIF_PLAYBACK.HOVER;
  }
  if ((Number(gifCount) || 0) > GIF_AUTOPLAY_MAX_FILLS) return GIF_PLAYBACK.HOVER;
  return GIF_PLAYBACK.AUTOPLAY;
}

/** Read the browser inputs of the policy (never throws). */
export function readGifPlaybackEnv(win = typeof window !== "undefined" ? window : undefined) {
  let reducedMotion = false;
  let deviceMemory;
  try {
    reducedMotion =
      !!win &&
      typeof win.matchMedia === "function" &&
      win.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch (e) {
    reducedMotion = false;
  }
  try {
    const dm = win && win.navigator && win.navigator.deviceMemory;
    if (typeof dm === "number") deviceMemory = dm;
  } catch (e) {
    deviceMemory = undefined;
  }
  return { reducedMotion, deviceMemory };
}

/** Policy for a renderer: count + live browser inputs, or an explicit override. */
export function resolveGifPlayback(gifCount, override) {
  if (override === GIF_PLAYBACK.AUTOPLAY || override === GIF_PLAYBACK.HOVER || override === GIF_PLAYBACK.STILL) {
    return override;
  }
  return gifPlaybackMode({ gifCount, ...readGifPlaybackEnv() });
}

// ── Still frames ────────────────────────────────────────────────────────────
// A browser ANIMATES a gif painted through an SVG <pattern><image> (measured:
// Chromium + WebKit, web-stg 2026-10-12), so "hover"/"still" need a real still
// image. Prefer the stored poster; else extract the first frame on a canvas
// (S3 serves Access-Control-Allow-Origin: *, so uploads extract cleanly).
// url -> data-URL (ready) | null (failed: host blocks CORS) ; pending tracked apart.
const stills = new Map();
const pending = new Map();
let extractor = extractGifPoster;

/** Test seam: swap the frame extractor (returns Promise<string|null>). */
export function __setGifStillExtractor(fn) {
  extractor = fn || extractGifPoster;
  stills.clear();
  pending.clear();
}

/**
 * The still frame for a gif url.
 * @returns {string|null|undefined} data URL when ready, null when extraction
 *   failed (caller falls back to the gif itself), undefined while pending —
 *   `onReady` fires once when it settles.
 */
export function gifStillFrame(url, onReady) {
  if (!url) return null;
  if (stills.has(url)) return stills.get(url);
  if (!pending.has(url)) {
    const listeners = new Set();
    pending.set(url, listeners);
    Promise.resolve()
      .then(() => extractor(url))
      .catch(() => null)
      .then((still) => {
        stills.set(url, still || null);
        pending.delete(url);
        listeners.forEach((fn) => {
          try {
            fn(still || null);
          } catch (e) {
            /* a torn-down renderer must not break the others */
          }
        });
      });
  }
  if (typeof onReady === "function") pending.get(url).add(onReady);
  return undefined;
}

/**
 * The url a gif wedge should PAINT for a playback mode.
 * @param {{gif:string, poster?:string}} media — gif = the animating url,
 *   poster = a stored still distinct from it (optional)
 * @returns {string|null|undefined} url to paint; undefined = still pending
 *   (paint the fallback colour), never the gif while a still is coming.
 */
export function gifPaintHref({ gif, poster }, mode, onReady) {
  if (mode === GIF_PLAYBACK.AUTOPLAY) return gif || poster || null;
  if (poster && poster !== gif) return poster;
  if (!gif) return poster || null;
  const still = gifStillFrame(gif, onReady);
  if (still === undefined) return undefined;
  // Extraction blocked (cross-origin host without CORS): the gif is the only
  // image there is — painting it beats an empty wedge.
  return still || gif;
}

const gifFillPlayback = {
  GIF_AUTOPLAY_MAX_FILLS,
  GIF_LOW_DEVICE_MEMORY_GB,
  GIF_PLAYBACK,
  gifPlaybackMode,
  readGifPlaybackEnv,
  resolveGifPlayback,
  gifStillFrame,
  gifPaintHref,
};
export default gifFillPlayback;
